import { useContext, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Container,
  Fade,
  IconButton,
  Paper,
  TextField,
  Tooltip,
} from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import EditIcon from "@mui/icons-material/Edit";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { Link, useNavigate } from "react-router-dom";
import { AlertComponent, DialogComponent, NavBar } from "../../components";
import { useFetch, usePermissions } from "../../hooks";
import { usePaginator } from "../../hooks/usePaginator";
import DataContext from "../../context/DataContext";
import { TallyListProps, TechListProps } from "../../interfaces/interfaces";
import {
  ListColumn,
  ListFilterField,
  ListPageLayout,
} from "../../components/shared/ListPageLayout";

type DesignRow = TechListProps | TallyListProps;
type DateRangePreset = "all" | "7" | "30" | "90" | "ytd" | "custom";

interface FetchResponse {
  success?: boolean;
  data: DesignRow[];
}

interface DesignKindConfig {
  paginatorKey: string;
  endpoint: string;
  navBarTitle: string;
  pageTitle: string;
  pageSubtitle: string;
  searchPlaceholder: string;
  emptyTitle: string;
  newRoute: string;
  detailRoute: (id: number | string) => string;
  salesRoute: (id: number | string) => string;
  convertRoute: string;
  convertLabel: string;
  deleteConfirmLabel: string;
}

// Every field below is a real difference found by diffing TechDesignList.tsx
// and TallyDesignList.tsx line by line — see task-22-report.md for the full
// list (labels, endpoints, routes, and which DataContext fetch/delete
// function each kind uses).
const CONFIG: Record<"tech" | "tally", DesignKindConfig> = {
  tech: {
    paginatorKey: "tech-design",
    endpoint: "proposal-detail",
    navBarTitle: "Technical Proposals",
    pageTitle: "Technical Proposals",
    pageSubtitle: "Browse, filter and manage technical proposals",
    searchPlaceholder: "Search proposals…",
    emptyTitle: "No proposals match your filters",
    newRoute: "/tech/0",
    detailRoute: (id) => `/tech/${id}`,
    salesRoute: (id) => `/sales/tech/${id}`,
    convertRoute: "/tally/0",
    convertLabel: "Create as Tally",
    deleteConfirmLabel: "Technical Design",
  },
  tally: {
    paginatorKey: "tally-design",
    endpoint: "tally-detail",
    navBarTitle: "Tally Designs",
    pageTitle: "Tally Designs",
    pageSubtitle: "Browse, filter and manage tally designs",
    searchPlaceholder: "Search tally designs…",
    emptyTitle: "No tally designs match your filters",
    newRoute: "/tally/0",
    detailRoute: (id) => `/tally/${id}`,
    salesRoute: (id) => `/sales/tally/${id}`,
    convertRoute: "/tech/0",
    convertLabel: "Create as Technical Design",
    deleteConfirmLabel: "Tally Design",
  },
};

const fmtDate = (d: string) => {
  if (!d) return "—";
  const dt = new Date(d.includes("T") ? d : d + "T00:00:00");
  if (isNaN(dt.getTime())) return "—";
  return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
};

const computeDateRange = (
  range: DateRangePreset,
  from: string,
  to: string,
): { dateFrom?: string; dateTo?: string } => {
  if (range === "all") return {};
  const today = new Date();
  const fmt = (d: Date) => d.toISOString().split("T")[0];
  if (range === "7") {
    const f = new Date(today);
    f.setDate(f.getDate() - 7);
    return { dateFrom: fmt(f) };
  }
  if (range === "30") {
    const f = new Date(today);
    f.setDate(f.getDate() - 30);
    return { dateFrom: fmt(f) };
  }
  if (range === "90") {
    const f = new Date(today);
    f.setDate(f.getDate() - 90);
    return { dateFrom: fmt(f) };
  }
  if (range === "ytd") return { dateFrom: `${today.getFullYear()}-01-01` };
  if (range === "custom") return { dateFrom: from || undefined, dateTo: to || undefined };
  return {};
};

interface Props {
  kind: "tech" | "tally";
}

export const DesignListPage = ({ kind }: Props) => {
  const config = CONFIG[kind];
  const navigate = useNavigate();
  const { paginationModel, setPaginationModel } = usePaginator(config.paginatorKey, 8);
  const {
    onResetValues,
    fetchDataTechProp,
    fetchDataTally,
    onDeleteProp,
    onDeleteTally,
    isSuccess,
    isError: isMutationError,
    clientOptions,
    wellOptions,
    fetchDataWells,
    fetchDataClient,
  } = useContext(DataContext);
  const { canManageDesigns } = usePermissions();

  const fetchDataForKind = kind === "tech" ? fetchDataTechProp : fetchDataTally;
  const onDeleteForKind = kind === "tech" ? onDeleteProp : onDeleteTally;

  const [idToDelete, setIdToDelete] = useState("");
  const [isOpenAlert, setIsOpenAlert] = useState(false);
  const [search, setSearch] = useState("");
  const [clientId, setClientId] = useState("");
  const [wellId, setWellId] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [dateRange, setDateRange] = useState<DateRangePreset>("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [userOptions, setUserOptions] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    fetchDataClient();
    const token = localStorage.getItem("info");
    fetch(`${process.env.REACT_APP_DEV_API}/user`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.success && r.data) setUserOptions(r.data.filter((u: any) => u.active && u.name));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseClient = (nextClientId: string) => {
    setClientId(nextClientId);
    setWellId("");
    if (nextClientId) fetchDataWells(Number(nextClientId));
    // Client lives in toolbarActions, not the `filters` array, so
    // ListPageLayout's own filterSignature effect won't reset the page for
    // it — reset explicitly here, same as PumpModuleAdmin's chooseClient.
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const fetchFilters = useMemo(() => {
    const { dateFrom, dateTo } = computeDateRange(dateRange, customFrom, customTo);
    return {
      search,
      client: clientId || undefined,
      well: wellId || undefined,
      createdBy: createdBy || undefined,
      dateFrom,
      dateTo,
    };
  }, [search, clientId, wellId, createdBy, dateRange, customFrom, customTo]);

  const { data, isLoading, error, fetchData, pagination } = useFetch<FetchResponse>(
    config.endpoint,
    paginationModel,
    undefined,
    undefined,
    fetchFilters,
  );
  const rows = data?.data ?? [];

  const columns: ListColumn<DesignRow>[] = [
    {
      key: "client",
      header: "Client",
      render: (r: any) => (
        <Box
          component={Link}
          to={config.detailRoute(r.id)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {r.well?.client?.name ?? "—"}
        </Box>
      ),
      sortAccessor: (r: any) => r.well?.client?.name ?? "",
      width: "1.2fr",
    },
    {
      key: "well",
      header: "Well",
      render: (r: any) => r.well?.name ?? "—",
      sortAccessor: (r: any) => r.well?.name ?? "",
      width: "1fr",
    },
    {
      key: "customName",
      header: "Description",
      render: (r) => r.customName ?? "—",
      sortAccessor: (r) => r.customName ?? "",
      width: "1.2fr",
    },
    {
      key: "date",
      header: "Date",
      render: (r) => fmtDate(r.date ?? ""),
      sortAccessor: (r) => r.date ?? "",
      width: "100px",
    },
    {
      key: "createdBy",
      header: "Created by",
      render: (r: any) => r.createdBy ?? "—",
      sortAccessor: (r: any) => r.createdBy ?? "",
      width: "130px",
    },
  ];

  const filters: ListFilterField[] = [
    {
      kind: "autocomplete",
      key: "well",
      label: "Well",
      value: wellId,
      onChange: setWellId,
      options: (wellOptions || []).map((w) => ({ value: String(w.id), label: w.name })),
      placeholder: clientId ? "Search wells…" : "Select a client",
      disabled: !clientId,
    },
    ...(userOptions.length > 0
      ? [
          {
            kind: "select" as const,
            key: "createdBy",
            label: "Created by",
            value: createdBy,
            onChange: setCreatedBy,
            options: userOptions.map((u) => ({ value: u.name, label: u.name })),
          },
        ]
      : []),
    {
      kind: "select",
      key: "dateRange",
      label: "Date range",
      value: dateRange,
      // ListPageLayout injects a blank "All" option ahead of these; map it back
      // onto the explicit "all" preset so the state always holds a real preset.
      onChange: (value) => setDateRange((value || "all") as DateRangePreset),
      options: [
        { value: "all", label: "All time" },
        { value: "7", label: "Last 7 days" },
        { value: "30", label: "Last 30 days" },
        { value: "90", label: "Last 90 days" },
        { value: "ytd", label: "This year" },
        { value: "custom", label: "Custom range" },
      ],
    },
    ...(dateRange === "custom"
      ? [
          { kind: "date" as const, key: "dateFrom", label: "From date", value: customFrom, onChange: setCustomFrom },
          { kind: "date" as const, key: "dateTo", label: "To date", value: customTo, onChange: setCustomTo },
        ]
      : []),
  ];

  return (
    <>
      <NavBar title={config.navBarTitle} buttons={[]} />
      <Box
        sx={{
          marginTop: "80px",
          minHeight: "calc(100vh - 80px)",
          background:
            "linear-gradient(135deg, rgba(241, 236, 231, 0.8) 0%, rgba(241, 236, 231, 0.4) 100%)",
          padding: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="xl">
          <Fade in={true} timeout={600}>
            <Paper
              elevation={3}
              sx={{
                padding: { xs: 2, md: 4 },
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(140, 150, 169, 0.1)",
                boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
              }}
            >
              <ListPageLayout<DesignRow>
                title={config.pageTitle}
                subtitle={config.pageSubtitle}
                columns={columns}
                rows={rows}
                getRowId={(r) => r.id}
                rowsLoading={isLoading}
                isError={Boolean(error)}
                errorMessage={`${config.pageTitle} could not be loaded.`}
                emptyTitle={config.emptyTitle}
                emptySubtitle="Try clearing a filter or selecting a different well."
                searchValue={search}
                onSearchChange={setSearch}
                searchPlaceholder={config.searchPlaceholder}
                filters={filters}
                page={paginationModel.page}
                pageSize={paginationModel.pageSize}
                totalCount={Number(pagination?.totalRecords || 0)}
                onPageChange={(page) => setPaginationModel({ ...paginationModel, page })}
                onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
                rowsPerPageOptions={[8, 15, 25, 50]}
                toolbarActions={
                  <Box sx={{ display: "flex", alignItems: "flex-end", gap: "10px" }}>
                    <Box sx={{ width: 220 }}>
                      <Box
                        component="label"
                        sx={{
                          display: "block",
                          mb: "5px",
                          color: "#9CA1AB",
                          fontSize: 10.5,
                          fontWeight: 600,
                          letterSpacing: ".6px",
                          lineHeight: 1.2,
                          textTransform: "uppercase",
                        }}
                      >
                        Client
                      </Box>
                      <Autocomplete
                        size="small"
                        options={clientOptions}
                        value={clientOptions.find((c) => String(c.id) === clientId) || null}
                        getOptionLabel={(c) => c.name}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        onChange={(_, c) => chooseClient(c ? String(c.id) : "")}
                        autoHighlight
                        openOnFocus
                        renderInput={(params) => <TextField {...params} placeholder="Search clients…" />}
                      />
                    </Box>
                    {canManageDesigns && (
                      <Button
                        startIcon={<AddBoxIcon />}
                        onClick={() => {
                          onResetValues();
                          navigate(config.newRoute);
                        }}
                      >
                        New
                      </Button>
                    )}
                  </Box>
                }
                rowActions={(r) => (
                  <>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => navigate(config.detailRoute(r.id))} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canManageDesigns && (
                      <Tooltip title="Duplicate">
                        <IconButton
                          onClick={() => {
                            fetchDataForKind(r.id);
                            navigate(config.newRoute);
                          }}
                          size="small"
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManageDesigns && (
                      <Tooltip title={config.convertLabel}>
                        <IconButton
                          onClick={() => {
                            fetchDataForKind(r.id, true);
                            navigate(config.convertRoute);
                          }}
                          size="small"
                        >
                          <SwapHorizIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManageDesigns && (
                      <Tooltip title="Generate Sales">
                        <IconButton onClick={() => navigate(config.salesRoute(r.id))} size="small">
                          <ReceiptLongIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Print">
                      <IconButton
                        component={Link}
                        to={`${config.detailRoute(r.id)}?pdf=true`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                      >
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canManageDesigns && (
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => {
                            setIdToDelete(String(r.id));
                            setIsOpenAlert(true);
                          }}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                )}
              />
            </Paper>
          </Fade>
        </Container>
      </Box>
      {isSuccess && <AlertComponent type="success" />}
      {isMutationError && <AlertComponent type="error" />}
      {isOpenAlert && (
        <DialogComponent
          type="error"
          modalContent={<div>Are you sure you want to remove this {config.deleteConfirmLabel}?</div>}
          onAccept={() => {
            onDeleteForKind(idToDelete);
            setIdToDelete("");
            setIsOpenAlert(false);
            fetchData();
          }}
          onCancel={() => {
            setIsOpenAlert(false);
            setIdToDelete("");
          }}
        />
      )}
    </>
  );
};
